import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Plane } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export const NewRelocGuide = ({ entry = null, onClose = null }) => {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [dest, setDest] = useState("");
  const [lists, setLists] = useState({ popular: [], popular_family: [] });
  const [family, setFamily] = useState(false);
  const list = family ? lists.popular_family : lists.popular;
  const [busy, setBusy] = useState(false);
  const shown = entry ? !!entry.id : open;
  useEffect(() => { if (shown) api.get("/relocation/destinations").then((r) => setLists(r.data)).catch(() => {}); }, [shown]);
  useEffect(() => { if (entry?.id) { setDest(entry.destination || ""); setFamily(false); } }, [entry]);
  const close = (o) => { if (o) return; entry ? onClose() : setOpen(false); };

  const go = async () => {
    if (entry && !window.confirm(`Start building the Australia → ${dest.trim()} ${family ? "Family" : "Standard"} Edition guide? It takes about 25–40 minutes and uses AI credit.`)) return;
    setBusy(true);
    try {
      const { data } = entry
        ? await api.post(`/relocation/register/${entry.id}/create-guide`, { destination: dest.trim(), family })
        : await api.post("/relocation/guides", { destination: dest, family });
      toast.success(`Building the ${dest}${family ? " families" : ""} guide`);
      nav(`/relocation/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={shown} onOpenChange={entry ? close : setOpen}>
      {!entry && <DialogTrigger asChild>
        <Button data-testid="reloc-new-guide" className="rounded-none h-12 px-6"><Plus className="w-4 h-4 mr-2" />New guide</Button>
      </DialogTrigger>}
      <DialogContent className="rounded-none bg-[#FAF7F2] max-w-lg" data-testid="reloc-new-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#3B1B48]">{entry ? `Create a guide from “${entry.name}”` : "New relocation guide"}</DialogTitle>
          <DialogDescription>Moving from Australia to… The studio researches verified sources and writes three volumes, a complete edition, a free sample, the mobile app (full guide + tools), artwork and listing.{entry ? " The register entry will be linked to the new guide." : ""}</DialogDescription>
        </DialogHeader>
<label className="flex items-center gap-2 text-sm text-[#3B1B48] cursor-pointer" data-testid="reloc-family-toggle">
          <input type="checkbox" checked={family} onChange={(e) => setFamily(e.target.checked)} className="accent-[#3B1B48] w-4 h-4" />
          Family Edition (written for parents moving with kids). Leave unticked for the Standard Edition
        </label>
        <div className="flex flex-wrap gap-2 max-h-44 overflow-auto" data-testid="reloc-popular">
          {list.map((c) => (
            <button key={c} data-testid={`reloc-pick-${c.replace(/\s+/g, "-")}`} onClick={() => setDest(c)}
              className={`px-3 py-1.5 text-sm border transition-colors ${dest === c ? "bg-[#3B1B48] text-[#FAF7F2] border-[#3B1B48]" : "border-[#E2D9CC] hover:bg-[#EFE6F3]"}`}>{c}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 border border-[#E2D9CC] bg-white px-3 mt-2">
          <Plane className="w-4 h-4 text-[#C59B27]" />
          <input data-testid="reloc-destination-input" value={dest} onChange={(e) => setDest(e.target.value)} placeholder="Or type any country" className="flex-1 h-11 outline-none text-sm bg-transparent" />
        </div>
        <p className="text-xs text-[#8C827A]">Takes about 25–40 minutes and uses AI credit for 24 chapters and 26 images. The guide stays a draft until you publish it.</p>
        <Button data-testid="reloc-build-start" onClick={go} disabled={busy || dest.trim().length < 2} className="rounded-none h-11">{busy ? "Starting…" : `Build Australia → ${dest.trim() || "…"}`}</Button>
      </DialogContent>
    </Dialog>
  );
};
